if (!customElements.get('v2-product-showcase')) {
  customElements.define('v2-product-showcase', class V2ProductShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.track = this.querySelector('[data-v2-track]');
      this.progress = this.querySelector('[data-v2-progress]');
      this.previousButton = this.querySelector('[data-v2-prev]');
      this.nextButton = this.querySelector('[data-v2-next]');
      this.viewMoreButton = this.querySelector('[data-v2-view-more]');
      this.mobilePageSize = Number(this.dataset.mobilePageSize) || 6;
      if (!this.track || !this.progress || !this.previousButton || !this.nextButton || !this.viewMoreButton) return;
      this.initialized = true;
      this.onClick = this.handleClick.bind(this);
      this.onScroll = this.updateProgress.bind(this);
      this.onResize = this.updateProgress.bind(this);
      this.onProgressInput = this.handleProgressInput.bind(this);
      this.onProgressChange = this.handleProgressChange.bind(this);
      this.onPointerLeave = this.handlePointerLeave.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerleave', this.onPointerLeave, true);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.progress.addEventListener('input', this.onProgressInput);
      this.progress.addEventListener('change', this.onProgressChange);
      window.addEventListener('resize', this.onResize);
      this.setupSwatches();
      if (!this.hasAttribute('data-design-mode')) this.loadProducts();
      this.updateProgress();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerleave', this.onPointerLeave, true);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.progress?.removeEventListener('input', this.onProgressInput);
      this.progress?.removeEventListener('change', this.onProgressChange);
      window.removeEventListener('resize', this.onResize);
      this.initialized = false;
    }

    async loadProducts() {
      try {
        if (this.dataset.mode !== 'recently_viewed') {
          if (!this.dataset.productId) {
            this.hidden = true;
            return;
          }
          const url = new URL(this.dataset.recommendationsUrl, location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('product_id', this.dataset.productId);
          if (this.dataset.mode === 'often_bought_with') {
            url.searchParams.set('intent', 'complementary');
            url.searchParams.set('limit', Math.min(Number(this.dataset.limit), 10));
          } else {
            url.searchParams.set('limit', this.dataset.limit);
          }
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
          const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = documentHtml.querySelector('[data-v2-track]');
          if (!source) throw new Error('Recommendations section was not returned');
          this.track.replaceChildren(...Array.from(source.querySelectorAll('[data-v2-card]')));
        } else {
          const key = `recently_viewed_handles_${this.dataset.shopId}`;
          let handles = [];
          try {
            const stored = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(stored)) handles = stored.filter((handle) => typeof handle === 'string');
          } catch (_) { /* Ignore old or invalid browser history. */ }
          handles = [...new Set(handles)].filter((handle) => handle !== this.dataset.currentHandle).slice(0, Number(this.dataset.limit));
          if (!handles.length) {
            this.hidden = true;
            return;
          }
          this.hidden = false;
          const cards = await Promise.all(handles.map(async (handle) => {
            if (!/^[a-z0-9-]+$/i.test(handle)) return null;
            try {
              const response = await fetch(`${this.dataset.rootUrl.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?view=v2-card`);
              if (!response.ok) return null;
              const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
              return documentHtml.querySelector('[data-v2-card]');
            } catch (_) { return null; }
          }));
          this.track.replaceChildren(...cards.filter(Boolean));
        }
        this.hidden = !this.track.querySelector('[data-v2-card]');
        this.setupSwatches();
        this.collapseForMobile();
        this.updateProgress();
      } catch (error) {
        console.warn('Product showcase could not load', error);
        this.hidden = true;
      } finally {
        this.track.removeAttribute('aria-busy');
      }
    }

    collapseForMobile() {
      if (this.dataset.mode !== 'recommendations') return;
      const cards = this.track.querySelectorAll('[data-v2-card]');
      cards.forEach((card, index) => card.toggleAttribute('data-v2-extra', index >= this.mobilePageSize));
      this.viewMoreButton.hidden = cards.length <= this.mobilePageSize;
    }

    showMoreCards() {
      const extras = Array.from(this.track.querySelectorAll('[data-v2-extra]'));
      extras.slice(0, this.mobilePageSize).forEach((card) => card.removeAttribute('data-v2-extra'));
      this.viewMoreButton.hidden = extras.length <= this.mobilePageSize;
    }

    updateProgress() {
      if (!this.track) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maxScroll > 1;
      this.progress.hidden = !hasOverflow;
      this.progress.disabled = !hasOverflow;
      this.progress.value = maxScroll ? String(Math.round(this.track.scrollLeft / maxScroll * 100)) : '0';
      this.previousButton.disabled = !hasOverflow || this.track.scrollLeft <= 1;
      this.nextButton.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 1;
    }

    handleProgressInput() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.classList.add('v2-showcase__track--dragging');
      this.track.scrollLeft = maxScroll * Number(this.progress.value) / 100;
    }

    handleProgressChange() {
      this.track.classList.remove('v2-showcase__track--dragging');
      this.updateProgress();
    }

    setupSwatches(root = this.track) {
      root.querySelectorAll('[data-v2-swatches]').forEach((container) => {
        const count = container.querySelectorAll('[data-v2-swatch]').length;
        if (count > 5 && !container.querySelector('[data-v2-more-colors]')) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'v2-showcase__more-colors';
          more.dataset.v2MoreColors = '';
          more.textContent = `+${count - 5}`;
          more.setAttribute('aria-expanded', 'false');
          container.append(more);
        }
        if (count > 3 && !container.querySelector('[data-v2-more-link]')) {
          const link = document.createElement('a');
          link.className = 'v2-showcase__more-link';
          link.dataset.v2MoreLink = '';
          link.href = container.closest('[data-v2-card]').dataset.url;
          link.textContent = `+${count - 3}`;
          link.setAttribute('aria-label', container.dataset.detailsLabel);
          container.append(link);
        }
      });
    }

    handleClick(event) {
      if (event.target.closest('[data-v2-view-more]')) {
        this.showMoreCards();
        return;
      }
      const navButton = event.target.closest('[data-v2-prev], [data-v2-next]');
      if (navButton && this.contains(navButton)) {
        const card = this.track.querySelector('[data-v2-card]');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        const cardsPerClick = window.matchMedia('(min-width: 900px)').matches ? 3 : 1;
        this.track.scrollBy({
          left: (navButton === this.nextButton ? 1 : -1) * step * cardsPerClick,
          behavior: 'smooth',
        });
        return;
      }
      const combinationSwatch = event.target.closest('[data-v2-combination-swatch]');
      if (combinationSwatch && this.contains(combinationSwatch)) {
        this.switchSwatch(combinationSwatch.closest('[data-v2-card]'), combinationSwatch);
        return;
      }
      const swatch = event.target.closest('[data-v2-swatch]');
      if (swatch && this.contains(swatch)) {
        this.switchSwatch(swatch.closest('[data-v2-card]'), swatch);
        return;
      }
      const more = event.target.closest('[data-v2-more-colors]');
      if (more && this.contains(more)) {
        const expanded = more.getAttribute('aria-expanded') !== 'true';
        more.setAttribute('aria-expanded', String(expanded));
        more.parentElement.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? '−' : `+${more.parentElement.querySelectorAll('[data-v2-swatch]').length - 5}`;
        return;
      }
    }

    switchSwatch(card, swatch) {
      if (!card || !swatch) return;
      card.querySelectorAll('[data-v2-swatch]').forEach((button) => button.setAttribute('aria-pressed', String(button === swatch)));
      const image = card.querySelector('.v2-showcase__image');
      if (image && swatch.dataset.image) {
        image.src = swatch.dataset.image;
        image.removeAttribute('srcset');
      }
      if (swatch.dataset.url) {
        card.dataset.url = swatch.dataset.url;
        card.querySelectorAll('.v2-showcase__image-link, .v2-showcase__name, [data-v2-more-link]').forEach((link) => { link.href = swatch.dataset.url; });
      }
      if (swatch.dataset.combinationHandle) card.dataset.handle = swatch.dataset.combinationHandle;
      if (swatch.dataset.title) {
        const name = card.querySelector('.v2-showcase__name');
        const imageLink = card.querySelector('.v2-showcase__image-link');
        const quickButton = card.querySelector('[data-v2-quick]');
        if (name) name.textContent = swatch.dataset.title;
        if (imageLink) imageLink.setAttribute('aria-label', swatch.dataset.title);
        if (quickButton) quickButton.setAttribute('aria-label', `${quickButton.dataset.quickShopLabel}: ${swatch.dataset.title}`);
      }
      const price = card.querySelector('[data-v2-price]');
      if (price && swatch.dataset.price) price.textContent = swatch.dataset.price;
      const discount = card.querySelector('[data-v2-discount]');
      if (discount) {
        discount.textContent = swatch.dataset.discount || '';
        discount.hidden = !swatch.dataset.discount;
      }
    }

    handlePointerLeave(event) {
      const card = event.target.closest('[data-v2-card]');
      if (!card || event.relatedTarget?.closest?.('[data-v2-card]') === card) return;
      card.querySelector(':focus')?.blur();
    }

  });
}
